import React, { useEffect, useState } from "react";
import { Sparkles, ImagePlus, Loader2, Send, X } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CATEGORIES = ["Govt & Health", "City", "Hospitals", "Management", "Opportunities"];

export function PublishUpdateModal({ open, onOpenChange, onPublished }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("Hospitals");
  const [hubSlug, setHubSlug] = useState("none");
  const [city, setCity] = useState("Ahmedabad");
  const [tags, setTags] = useState("");
  const [cover, setCover] = useState("");
  const [summary, setSummary] = useState("");
  const [hubs, setHubs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) api.get("/hubs").then(({ data }) => setHubs(data)).catch(() => {});
  }, [open]);

  const reset = () => {
    setTitle(""); setBody(""); setCategory("Hospitals"); setHubSlug("none");
    setCity("Ahmedabad"); setTags(""); setCover(""); setSummary("");
  };

  const isGovt = category === "Govt & Health";

  const runAI = async () => {
    if (!body.trim()) { toast.info("Write the update body first"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/summarize", { title, body });
      if (data.summary) setSummary(data.summary);
      if (data.tags && data.tags.length) setTags(data.tags.join(", "));
      if (data.suggested_category && CATEGORIES.includes(data.suggested_category)) setCategory(data.suggested_category);
      toast.success("AI summary & tags added");
    } catch {
      toast.error("AI assistant unavailable right now");
    } finally { setAiBusy(false); }
  };

  const uploadCover = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setCover(data.url);
      toast.success("Cover image added");
    } catch {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  };

  const publish = async () => {
    if (!title.trim() || !body.trim()) { toast.info("Add a headline and body"); return; }
    setBusy(true);
    try {
      await api.post("/updates", {
        title, body, category,
        is_govt_health: isGovt,
        hub_slug: isGovt && hubSlug !== "none" ? hubSlug : null,
        city, summary,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        cover_url: cover || null,
      });
      toast.success("Update published");
      reset();
      onOpenChange(false);
      onPublished && onPublished();
    } catch (e) {
      toast.error("Could not publish");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="publish-update-modal" className="max-h-[90vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Publish a News Update</DialogTitle>
          <DialogDescription className="sr-only">Create and publish a healthcare news update for the Ahmedabad network.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold text-slate-600">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="publish-category-select" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {isGovt && (
            <div>
              <Label className="text-xs font-semibold text-slate-600">Link to Govt & Health hub (optional)</Label>
              <Select value={hubSlug} onValueChange={setHubSlug}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select a hub" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No hub</SelectItem>
                  {hubs.map((h) => <SelectItem key={h.slug} value={h.slug}>{h.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <Input data-testid="publish-city-input" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City (e.g. Ahmedabad)" />
          <Input data-testid="publish-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Headline (e.g. New 150-bed hospital unit announced)" />
          <Textarea data-testid="publish-body-input" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write the body of this news update…" className="min-h-[110px]" />

          <button data-testid="ai-assist-button" onClick={runAI} disabled={aiBusy}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-medorange/50 bg-medorange-light py-2.5 text-sm font-semibold text-medorange transition-colors hover:bg-orange-100 disabled:opacity-60">
            {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {aiBusy ? "Thinking…" : "AI: summarize & suggest tags"}
          </button>

          {summary && (
            <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
              <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">AI summary</p>{summary}
            </div>
          )}

          <Input data-testid="publish-tags-input" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="Tags, comma separated (e.g. Cardiology, Regulation)" />

          {cover && (
            <div className="relative">
              <img src={fileUrl(cover)} alt="cover" className="h-36 w-full rounded-xl object-cover" />
              <button onClick={() => setCover("")} className="absolute right-2 top-2 rounded-full bg-black/60 p-1 text-white"><X className="h-4 w-4" /></button>
            </div>
          )}

          <div className="flex items-center justify-between">
            <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-500 hover:text-navy">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
              Add cover image
              <input type="file" accept="image/*" className="hidden" onChange={uploadCover} />
            </label>
            <Button data-testid="publish-update-submit-button" onClick={publish} disabled={busy} className="gap-1.5 rounded-full bg-medorange px-5 hover:bg-medorange-hover">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Publish
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
